import { useState } from 'react';
import { MediaPicker, type MediaPickerValue } from '@/components/media/MediaPicker';

export interface PdfFolderFile {
  mediaFileId: string;
  title: string;
}

export function PdfFolderField({
  files,
  onChange,
}: {
  files: PdfFolderFile[];
  onChange: (files: PdfFolderFile[]) => void;
}) {
  const [draft, setDraft] = useState<MediaPickerValue | null>(null);

  function addDraft(next: MediaPickerValue | null) {
    setDraft(null);
    if (!next || files.some((file) => file.mediaFileId === next.id)) return;
    onChange([...files, { mediaFileId: next.id, title: next.title }]);
  }

  function moveFile(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= files.length) return;
    const next = files.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChange(next);
  }

  return (
    <div className="space-y-4">
      {files.length === 0 ? (
        <p className="text-sm text-dash-on-surface-variant">Esta carpeta todavía no tiene PDF.</p>
      ) : (
        <ol className="space-y-2">
          {files.map((file, index) => (
            <li
              key={file.mediaFileId}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-dash-outline-variant bg-white p-3"
            >
              <span className="material-symbols-outlined text-dash-secondary" aria-hidden="true">
                picture_as_pdf
              </span>
              <input
                type="text"
                value={file.title}
                aria-label={`Título del PDF ${index + 1}`}
                className="min-w-0 flex-1 rounded-md border border-dash-outline-variant px-3 py-2 text-sm"
                onChange={(event) =>
                  onChange(
                    files.map((entry) =>
                      entry.mediaFileId === file.mediaFileId ? { ...entry, title: event.target.value } : entry,
                    ),
                  )
                }
              />
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded-md p-1 text-dash-outline hover:bg-[#f3f3f6] disabled:opacity-40"
                  aria-label="Subir PDF"
                  disabled={index === 0}
                  onClick={() => moveFile(index, -1)}
                >
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    arrow_upward
                  </span>
                </button>
                <button
                  type="button"
                  className="rounded-md p-1 text-dash-outline hover:bg-[#f3f3f6] disabled:opacity-40"
                  aria-label="Bajar PDF"
                  disabled={index === files.length - 1}
                  onClick={() => moveFile(index, 1)}
                >
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    arrow_downward
                  </span>
                </button>
                <button
                  type="button"
                  className="rounded-md p-1 text-red-600 hover:bg-red-50"
                  aria-label="Quitar PDF"
                  onClick={() => onChange(files.filter((entry) => entry.mediaFileId !== file.mediaFileId))}
                >
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    delete
                  </span>
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
      <MediaPicker label="Agregar PDF" value={draft} onChange={addDraft} filterType="pdf" />
    </div>
  );
}
